import { FC, useState } from "react";
import Head from "next/head";
import Link from "next/link";
import Image from "next/image";
import styles from "./styles.module.css";

type Faq = { q: string; a: string };

export const FAQ: Faq[] = [
  {
    q: "How does the flat fee work?",
    a: "0.05 SOL is the entire price of a Standard mint. It includes the custom mint address, on-chain social metadata, your choice of the Token-2022 or legacy SPL token program, and revocation of mint, freeze, and update authorities. The fee is charged once when the mint is created. No percentage of your supply is taken.",
  },
  {
    q: "Do you hold my keys or my SOL?",
    a: "No. SoulMinter is a non-custodial software tool. The transaction is built in your browser and signed by your own Solana wallet. We never see, store, or transmit your private keys, seed phrase, or signed transaction material.",
  },
  {
    q: "Who is behind SoulMinter?",
    a: "SoulMinter is operated by ROTH MATE EV, a Hungarian sole proprietorship (egyéni vállalkozó), tax number 91818627-1-28, based in the European Union. We are a software tool, not a crypto-asset service provider (CASP) under MiCA. We do not issue, hold, exchange, or recommend any token.",
  },
  {
    q: "What happens after I click 'Open the Token Studio'?",
    a: "You'll see the token configuration form. Fill in name, ticker, supply, decimals, logo and socials, then connect a Solana wallet and sign one transaction. The whole flow takes about a minute end to end.",
  },
];

const PROBLEM_ROWS: { label: string; soul: string; others: string }[] = [
  { label: "Base price", soul: "0.05 SOL", others: "0.05 – 0.15 SOL" },
  { label: "Custom mint address", soul: "Included", others: "+0.1 – 0.3 SOL" },
  { label: "Social metadata", soul: "Included", others: "+0.05 – 0.2 SOL" },
  { label: "Revoke authorities", soul: "Included", others: "+0.1 – 0.3 SOL" },
  { label: "Percentage of supply", soul: "None", others: "Sometimes 1 – 5%" },
  { label: "Typical total", soul: "0.05 SOL", others: "0.5 – 1.0 SOL" },
];

export const StartView: FC = () => {
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  return (
    <div className={styles.root}>
      <Head>
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link
          rel="stylesheet"
          href="https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,400;0,9..144,500;0,9..144,600;1,9..144,400;1,9..144,500&family=Manrope:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap"
        />
      </Head>
      <div className={styles.grain} aria-hidden="true" />
      <div className={styles.gradient} aria-hidden="true" />

      <header className={styles.header}>
        <Link href="/" className={styles.brand}>
          <Image
            src="/logo-mark.png"
            alt="SoulMinter"
            width={28}
            height={28}
            className={styles.brandMark}
          />
          <span className={styles.brandWord}>SoulMinter</span>
        </Link>
        <div className={styles.operator}>
          <span className={styles.operatorDot} aria-hidden="true" />
          <span>
            Operated by <strong>ROTH MATE EV</strong> · Hungary, EU · Tax ID 91818627-1-28
          </span>
        </div>
      </header>

      <main className={styles.main}>
        <section className={styles.hero}>
          <div className={styles.heroInner}>
            <span className={styles.eyebrow}>Solana token creator · flat-fee software</span>
            <h1 className={styles.h1}>
              Create a Solana token{" "}
              <em className={styles.h1Italic}>at one flat fee</em>.
            </h1>
            <p className={styles.lede}>
              SoulMinter creates SPL and Token-2022 mints for a single flat fee of{" "}
              <span className={styles.ledeBadge}>0.05 SOL</span>. The custom mint address,
              on-chain social metadata, and authority revocation are included. No account
              is required, and no percentage of your supply is taken.
            </p>

            <div className={styles.ctaRow}>
              <Link href="/create/standard" className={styles.ctaPrimary}>
                Open the Token Studio
                <span className={styles.ctaArrow} aria-hidden="true">→</span>
              </Link>
              <Link href="/create" className={styles.ctaSecondary}>
                Compare Standard &amp; Pro
              </Link>
            </div>

            <ul className={styles.trustChips}>
              <li>
                <span className={styles.chipLabel}>Non-custodial</span>
                <span className={styles.chipBody}>Your wallet signs locally. We never hold your keys or your SOL.</span>
              </li>
              <li>
                <span className={styles.chipLabel}>Software tool</span>
                <span className={styles.chipBody}>Not a CASP under MiCA. We do not issue, hold, exchange, or recommend any token.</span>
              </li>
              <li>
                <span className={styles.chipLabel}>One flat fee</span>
                <span className={styles.chipBody}>The 0.05 SOL fee is charged once, when the mint is created successfully.</span>
              </li>
            </ul>
          </div>
        </section>

        <section className={styles.compareSection} aria-label="Cost comparison">
          <div className={styles.sectionHead}>
            <span className={styles.sectionTag}>Cost breakdown</span>
            <h2 className={styles.h2}>
              How other Solana token creators price the same job.
            </h2>
            <p className={styles.sectionLede}>
              Ranges below are based on publicly listed pricing from other Solana token
              creators as of 2026. They show the difference between an advertised base
              price and the typical total after standard features are added.
            </p>
          </div>

          <div className={styles.compareCard}>
            <table className={styles.compareTable}>
              <thead>
                <tr>
                  <th scope="col">Item</th>
                  <th scope="col" className={styles.colSoul}>SoulMinter</th>
                  <th scope="col">Typical competitor</th>
                </tr>
              </thead>
              <tbody>
                {PROBLEM_ROWS.map((row, i) => (
                  <tr key={row.label} className={i === PROBLEM_ROWS.length - 1 ? styles.totalRow : undefined}>
                    <th scope="row">{row.label}</th>
                    <td className={styles.colSoul}>{row.soul}</td>
                    <td className={styles.colOther}>{row.others}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className={styles.compareNote}>
              Ranges reflect publicly listed pricing as of 2026. Your actual cost depends
              on which features your token needs.
            </p>
          </div>
        </section>

        <section className={styles.howSection} aria-label="How it works">
          <div className={styles.sectionHead}>
            <span className={styles.sectionTag}>How it works</span>
            <h2 className={styles.h2}>Three steps, about a minute.</h2>
          </div>

          <ol className={styles.steps}>
            <li className={styles.step}>
              <span className={styles.stepNum}>01</span>
              <h3 className={styles.stepTitle}>Open the Token Studio</h3>
              <p className={styles.stepBody}>
                Fill in the basics — name, ticker, supply, decimals, logo, socials. Pick a
                vanity prefix or suffix for your mint address if you want one. No account
                required.
              </p>
            </li>
            <li className={styles.step}>
              <span className={styles.stepNum}>02</span>
              <h3 className={styles.stepTitle}>Connect a Solana wallet</h3>
              <p className={styles.stepBody}>
                Any wallet that follows the Solana Wallet Standard works. Connection and
                signing happen in your wallet, locally, in your browser.
              </p>
            </li>
            <li className={styles.step}>
              <span className={styles.stepNum}>03</span>
              <h3 className={styles.stepTitle}>Sign one transaction</h3>
              <p className={styles.stepBody}>
                Your token lands on Solana mainnet in about a minute — fully on-chain,
                fully yours. Authorities are revoked in the same transaction if you chose to.
              </p>
            </li>
          </ol>
        </section>

        <section className={styles.faqSection} aria-label="Frequently asked questions">
          <div className={styles.sectionHead}>
            <span className={styles.sectionTag}>Questions, answered</span>
            <h2 className={styles.h2}>The four things people ask before they sign.</h2>
          </div>

          <ul className={styles.faqList}>
            {FAQ.map((item, i) => {
              const isOpen = openFaq === i;
              return (
                <li key={item.q} className={`${styles.faqItem} ${isOpen ? styles.faqOpen : ""}`}>
                  <button
                    type="button"
                    className={styles.faqQ}
                    aria-expanded={isOpen}
                    aria-controls={`start-faq-${i}`}
                    onClick={() => setOpenFaq(isOpen ? null : i)}
                  >
                    <span className={styles.faqIndex}>{String(i + 1).padStart(2, "0")}</span>
                    <span className={styles.faqQText}>{item.q}</span>
                    <span className={styles.faqToggle} aria-hidden="true">{isOpen ? "−" : "+"}</span>
                  </button>
                  <div id={`start-faq-${i}`} role="region" className={styles.faqA} hidden={!isOpen}>
                    <p>{item.a}</p>
                  </div>
                </li>
              );
            })}
          </ul>
        </section>

        <section className={styles.finalCta} aria-label="Open the token studio">
          <div className={styles.finalCtaInner}>
            <h2 className={styles.finalCtaTitle}>
              Ready when you are.
            </h2>
            <p className={styles.finalCtaBody}>
              No sign-up, no email. One flat fee of 0.05 SOL, charged once when the mint
              is created successfully.
            </p>
            <Link href="/create/standard" className={styles.ctaPrimary}>
              Open the Token Studio
              <span className={styles.ctaArrow} aria-hidden="true">→</span>
            </Link>
          </div>
        </section>
      </main>

      <footer className={styles.footer}>
        <div className={styles.footerInner}>
          <div className={styles.footerCol}>
            <span className={styles.footerBrand}>SoulMinter</span>
            <p className={styles.footerCopy}>
              A non-custodial software interface for creating SPL and Token-2022 mints on
              the Solana blockchain. SoulMinter is a software tool — not a crypto-asset
              service provider under Regulation (EU) 2023/1114 (MiCA). We do not issue,
              hold, exchange, or recommend any crypto-asset.
            </p>
          </div>
          <div className={styles.footerCol}>
            <span className={styles.footerLabel}>Operator</span>
            <p className={styles.footerOperator}>
              <strong>ROTH MATE EV</strong>
              <br />
              Hungarian sole proprietorship (egyéni vállalkozó)
              <br />
              Tax number: 91818627-1-28
              <br />
              <a href="mailto:main@soulminter.io" className={styles.footerLink}>main@soulminter.io</a>
            </p>
          </div>
          <div className={styles.footerCol}>
            <span className={styles.footerLabel}>Legal</span>
            <ul className={styles.footerLinks}>
              <li><Link href="/tos" className={styles.footerLink}>Terms of Service</Link></li>
              <li><Link href="/privacy" className={styles.footerLink}>Privacy Policy</Link></li>
              <li><Link href="/disclaimer" className={styles.footerLink}>Disclaimer</Link></li>
            </ul>
          </div>
        </div>
        <div className={styles.footerStamp}>
          <span>© 2026 ROTH MATE EV</span>
          <span aria-hidden="true">·</span>
          <span>Built in Sopron, Hungary</span>
        </div>
      </footer>
    </div>
  );
};
